import { color } from "d3-color";
import { GeoJsonLayerProps } from "@deck.gl/layers";
import { MVTLayer, MVTLayerProps } from "@deck.gl/geo-layers";
import { Color } from "@deck.gl/core";

import { ENVIRONMENT } from "src/js/util/environment";
import { SitesGeoJsonLayer } from "react-migration/layouts/map/Multilayer/layer_types/SitesLayerType/SitesGeoJsonLayer";
import {
  SiteMVTFeature,
  SiteMVTFeatureProperties,
} from "react-migration/layouts/map/Multilayer/layer_types/SitesLayerType/utils";
import { SHARED_SITE_COLOR, TRANSPARENT } from "../constants";

const SITES_TILE_URL_TEMPLATE =
  ENVIRONMENT.APP_ENV === "local"
    ? `${ENVIRONMENT.SITES_SERVICE_URL}/tiles/{z}/{x}/{y}.mvt`
    : `${ENVIRONMENT.SUPPORTING_SERVICE_URL}/sites-layer/tiles/{z}/{x}/{y}`;

type SitesMVTLayerProps = Omit<
  MVTLayerProps<SiteMVTFeatureProperties>,
  | "data"
  | "binary"
  | "filled"
  | "stroked"
  | "refinementStrategy"
  | "getFillColor"
  | "renderSubLayers"
  | "extensions"
  | "getFilterValue"
  | "filterRange"
> & {
  stageLookup: Record<string, { color: string }>;
  sitesDirtyCount: number;
  showSharedSites: boolean;
  workspaceId?: string | null;
  onFeatureHover?: (featureId: string | null) => void;
};

export class SitesMVTLayer extends MVTLayer<SiteMVTFeatureProperties, SitesMVTLayerProps> {
  static readonly layerName = "SitesMVTLayer";

  static getTileUrl(workspaceId?: string | null) {
    const searchParams = new URLSearchParams();

    if (workspaceId) {
      searchParams.append("workspaceId", workspaceId);
    }

    return `${SITES_TILE_URL_TEMPLATE}?${searchParams.toString()}`;
  }

  static getLineColor(feature: SiteMVTFeature, stageLookup?: SitesMVTLayerProps["stageLookup"]): Color {
    if (feature.properties.shared) {
      return SHARED_SITE_COLOR;
    }

    const stageColor = stageLookup?.[feature.properties.stage]?.color;
    if (!stageColor) {
      return TRANSPARENT;
    }

    try {
      const rgb = color(stageColor)?.rgb();
      return rgb ? [rgb.r, rgb.g, rgb.b, 255] : TRANSPARENT;
    } catch (error) {
      console.error("Failed to parse stage colour", error);
      return SHARED_SITE_COLOR;
    }
  }

  constructor(props: SitesMVTLayerProps) {
    const { workspaceId, stageLookup, sitesDirtyCount, updateTriggers, showSharedSites, onFeatureHover } = props;

    super(
      {
        data: SitesMVTLayer.getTileUrl(workspaceId),
        binary: true,
        filled: true,
        stroked: true,
        refinementStrategy: "no-overlap",
        minZoom: 0,
        pickable: true,
        getFillColor: TRANSPARENT,
        getLineColor: (d) => SitesMVTLayer.getLineColor(d, stageLookup),
        lineWidthUnits: "pixels",
        pointRadiusUnits: "pixels",
        getLineWidth: 3,
        onHover: (info) => {
          onFeatureHover?.(info?.object?.properties?.id ?? null);
        },
        renderSubLayers: (subLayerProps: GeoJsonLayerProps<SiteMVTFeatureProperties>) =>
          new SitesGeoJsonLayer(subLayerProps),
      },

      // Ensure user defined props are merged on top of defaults
      props,

      {
        updateTriggers: {
          getLineColor: [stageLookup, sitesDirtyCount, ...(updateTriggers?.getLineColor ?? [])],
          getTileData: [sitesDirtyCount, workspaceId, ...(updateTriggers?.getTileData ?? [])],
          getFilterValue: [showSharedSites, ...(updateTriggers?.getFilterValue ?? [])],
        },
      }
    );
  }
}
